import { useWizardStore } from '../../store/wizardStore'
import { Button } from '../ui/Button'
import { CardHeader, CardTitle, CardContent } from '../ui/Card'
import type { PickupLocation } from '../../lib/types'
import { clsx } from 'clsx'

import { useState } from 'react'
import { api } from '../../lib/api'

export default function Step3PickupLocation() {
  const store = useWizardStore()
  const { pickupLocation, setPickupLocation, gifts, orderId, nextStep, previousStep, updateGift } = store
  const [isUpdating, setIsUpdating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSelect = async (location: PickupLocation) => {
    setError(null)

    // If changing location and there are existing gifts, update them
    if (pickupLocation !== location && gifts.length > 0 && orderId) {
      setIsUpdating(true)
      try {
        await api.updatePickupLocation(orderId, location)

        // Update gifts in wizard store to reflect new pickup location
        gifts.forEach((gift, index) => {
          updateGift(index, {
            ...gift,
            pickupLocation: location,
          })
        })
      } catch (err) {
        console.error('Failed to update pickup location:', err)
        setError('Failed to update pickup location. Please try again.')
        setIsUpdating(false)
        return
      }
      setIsUpdating(false)
    }

    setPickupLocation(location)
    nextStep()
  }

  return (
    <>
      <CardHeader>
        <CardTitle>Where will you pick up your gifts?</CardTitle>
        <p className="text-gray-600 mt-2">
          Choose your preferred collection location.
        </p>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {error && (
            <div role="alert" className="bg-primary-50 border border-primary-300 p-4 rounded-lg">
              <p className="text-primary-800">{error}</p>
            </div>
          )}

          <button
            onClick={() => handleSelect('fayre')}
            aria-pressed={pickupLocation === 'fayre'}
            disabled={isUpdating}
            className={clsx(
              "w-full p-6 text-left border-2 rounded-lg transition-all active:scale-[0.98] motion-reduce:transition-none motion-reduce:active:scale-100",
              isUpdating && "opacity-50 cursor-not-allowed",
              pickupLocation === 'fayre'
                ? "border-accent-800 bg-accent-50"
                : "border-gray-200 hover:border-accent-400 hover:bg-accent-50"
            )}
          >
            <span className="flex justify-between items-start">
              <span>
                <span className="block text-lg font-semibold text-gray-900">We will be attending the fayre</span>
                <span className="block text-sm text-gray-600 mt-1">
                  Collect your gifts at the Christmas fayre
                </span>
              </span>
              {pickupLocation === 'fayre' && (
                <svg aria-hidden="true" className="w-6 h-6 text-accent-800 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              )}
            </span>
          </button>
          <button
            onClick={() => handleSelect('school')}
            aria-pressed={pickupLocation === 'school'}
            disabled={isUpdating}
            className={clsx(
              "w-full p-6 text-left border-2 rounded-lg transition-all active:scale-[0.98] motion-reduce:transition-none motion-reduce:active:scale-100",
              isUpdating && "opacity-50 cursor-not-allowed",
              pickupLocation === 'school'
                ? "border-accent-800 bg-accent-50"
                : "border-gray-200 hover:border-accent-400 hover:bg-accent-50"
            )}
          >
            <span className="flex justify-between items-start">
              <span>
                <span className="block text-lg font-semibold text-gray-900">We will NOT be attending the fayre</span>
                <span className="block text-sm text-gray-600 mt-1">
                  Collect your gifts from school at a later date
                </span>
              </span>
              {pickupLocation === 'school' && (
                <svg aria-hidden="true" className="w-6 h-6 text-accent-800 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              )}
            </span>
          </button>
          <div className="pt-4">
            <Button
              onClick={previousStep}
              fullWidth
              variant="ghost"
            >
              Back
            </Button>
          </div>
        </div>
      </CardContent>
    </>
  )
}
